import { JSX, useContext, useEffect, useMemo, useRef, useState } from "react";
import { ColumnConfig } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { TerminationsWizardStepProps, BulkProviderTerminationMethod, BulkProjectTypeEnum } from "@app/terminations/import/interfaces";
import { BulkProviderChangeProjectTableData, ProjectView } from "@app/shared/provider-change-wizards/interfaces";
import { TerminationsWizardContext } from "@app/terminations/import/TerminationsWizardContext";
import { ClaimsActivityLineOfBusinessTypeEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import PageSection from "@app/shared/PageSection";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { QESRadioButton } from "@app/shared/qes-standards/forms/RadioButton";
import { QESRadioGroup } from "@app/shared/qes-standards/forms/RadioGroup";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { TagGroupData, useSelectProjectByTags } from "@app/shared/provider-change-wizards/useSelectProjectsByTag";
import { ProjectOrTagSearchBox } from "@app/shared/provider-change-wizards/ProjectOrTagSearchBox";

const countyProjectsTableColumnConfig: ColumnConfig = {
  details: {
    displayText: "Details",
    testId: "details",
  },
  numberOfProjects: {
    displayText: "Number of Projects",
    testId: "number-of-projects",
  },
  projectId: {
    displayText: "",
    hidden: true,
    testId: "",
  },
  projectName: {
    displayText: "Project Name",
    testId: "project-name",
  },
  rowIsDisabled: {
    displayText: "",
    hidden: true,
    testId: "",
  },
  tagGroupName: {
    displayText: "Tag Group",
    testId: "tag-group",
  },
  tagName: {
    displayText: "Tag",
    noSort: true,
    testId: "tag",
  },
  templateName: {
    displayText: "Template",
    testId: "template",
  },
};

const zipProjectsTableColumnConfig: ColumnConfig = {
  details: {
    displayText: "Details",
    testId: "details",
  },
  numberOfProjects: {
    displayText: "Number of Projects",
    testId: "number-of-projects",
  },
  projectId: {
    displayText: "",
    hidden: true,
    testId: "",
  },
  projectName: {
    displayText: "Project",
    testId: "project-name",
  },
  rowIsDisabled: {
    displayText: "",
    hidden: true,
    testId: "",
  },
  tagGroupName: {
    displayText: "Tag Group",
    testId: "tag-group",
  },
  tagName: {
    displayText: "Tag",
    noSort: true,
    testId: "tag",
  },
  templateName: {
    displayText: "Template",
    testId: "template",
  },
};

const TerminationsWizardSelectProjects = (props: TerminationsWizardStepProps): JSX.Element => {
  const wizardContext = useContext(TerminationsWizardContext);
  const isZip = wizardContext.projectType === BulkProjectTypeEnum.Zip;
  const projectsTableColumnConfig = isZip ? zipProjectsTableColumnConfig : countyProjectsTableColumnConfig;
  const [searchString, setSearchString] = useState("");
  const [filteredView, setFilteredView] = useState<ProjectView>();
  const [view, setView] = useState<ProjectView>("projects");
  const searchRef = useRef<HTMLInputElement>(null);
  const {
    getEligibleSelectedProjectIds,
    handleTagCheckboxChange,
    indeterminateTags,
    isTagApiCallResolved,
    renderedExpansionData,
    renderedTags,
    selectedTags,
    tagExpansionData,
    tagGroupData,
  } = useSelectProjectByTags(wizardContext.projects, searchString, filteredView, wizardContext.totalSelectedProjectIds, wizardContext.setEligibleSelectedProjectIds, wizardContext.setTotalSelectedProjectIds);

  useEffect(() => {
    // fetch data from api
    wizardContext.providerTerminationService.getBulkTerminationProjects(
      wizardContext.bulkProviderTerminationMethod === BulkProviderTerminationMethod.Ghost,
      wizardContext.projectType ?? BulkProjectTypeEnum.County
    ).then((projectsResponse) => {

      const projectNamesToIds: Record<string, number> = {};

      // projects
      const projectsData: BulkProviderChangeProjectTableData[] = projectsResponse.map((project) => {
        projectNamesToIds[project.projectName] = project.projectId;
        const detailsArr: string[] = [];
        if (project.isReadOnly) {
          detailsArr.push("Read Only");
        }
        if (project.hasTerminations) {
          detailsArr.push("Existing Terminations");
        }
        if (!project.hasSuccessfulLatestRun && wizardContext.bulkProviderTerminationMethod === BulkProviderTerminationMethod.Ghost) {
          detailsArr.push("Failed Latest Run");
        }
        const detailsStr = detailsArr.join(" and ");
        return {
          /* oxlint-disable sort-keys */
          rowIsDisabled: project.isReadOnly || (!project.hasSuccessfulLatestRun && wizardContext.bulkProviderTerminationMethod === BulkProviderTerminationMethod.Ghost),
          key: project.projectId.toString(),
          projectId: project.projectId,
          projectName: project.projectName,
          templateName: project.templateName,
          details: detailsStr,
          vaClaimsColumnEnabled: project.vaClaimsColumnEnabled,
          tricareClaimsColumnEnabled: project.tricareClaimsColumnEnabled,
          /* oxlint-enable sort-keys */
        };
      });


      wizardContext.setProjects(projectsData);
    });
  }, []);

  useEffect(() => {
    if (!!wizardContext.selectedLineOfBusiness) {
      const updatedProjects = wizardContext.projects.map((project) => {
        if ((!project.vaClaimsColumnEnabled && wizardContext.selectedLineOfBusiness === "VA") || (!project.tricareClaimsColumnEnabled && wizardContext.selectedLineOfBusiness === "TRICARE")) {
          return {
            ...project,
            rowIsDisabled: true
          };
        } else {
          return {
            ...project,
            rowIsDisabled: !(project.details === "Existing Terminations" || project.details === "")
          };
        }
      });
      wizardContext.setProjects(updatedProjects);
      wizardContext.setEligibleSelectedProjectIds([]);
      wizardContext.setTotalSelectedProjectIds([]);
    }
  }, [wizardContext.selectedLineOfBusiness])

  const renderedProjects = useMemo(() => {
    if (!searchString) {
      return wizardContext.projects.sort((a, b) => a.projectName > b.projectName ? 1 : -1);
    }

    const regex = new RegExp(searchString, "i");
    if (filteredView === "projects") {
      const filteredProjectList = wizardContext.projects.filter((project) => !!project.projectName.match(regex));
      return filteredProjectList.sort((a, b) => a.projectName > b.projectName ? 1 : -1);
    } else if (filteredView === "tags") {
      let filteredProjectNames: string[] = [];
      tagExpansionData.forEach((value) => {
        filteredProjectNames = filteredProjectNames.concat(...value.filter((v) => v.hiddenTagGroupName.match(regex) || v.tagName.match(regex)).map((v) => v.projectNames));
      })
      filteredProjectNames = [...new Set(filteredProjectNames)];
      return wizardContext.projects.filter((p) => filteredProjectNames.includes(p.projectName));
    }
  }, [searchString, wizardContext.projects, filteredView, tagExpansionData]);

  const handleProjectCheckboxChange = (selectedKeys: string[], isBulk?: boolean) => {
    // if the click is from the bulk checkbox, we need to make sure we only change _visible_ projects
    if (isBulk) {
      let newSelectedProjectIds = [...wizardContext.totalSelectedProjectIds];
      // the bulk checkbox is all or nothing, so it's easy to tell if things are being added or removed
      if (selectedKeys.length > 0) {
        newSelectedProjectIds.push(...selectedKeys);
      } else {
        // iterate over the removedIds instead of newSelectedProjectIds,
        // because removing something from newSelectedProjectIds while iterating over it
        // is liable to cause issues
        const removedIds = renderedProjects.map((project) => project.projectId.toString());
        newSelectedProjectIds = newSelectedProjectIds.filter((id) => { return !removedIds.includes(id); });
      }
      const newTotalSelectedProjectIds = [...new Set(newSelectedProjectIds)]
      wizardContext.setTotalSelectedProjectIds(newTotalSelectedProjectIds);
      wizardContext.setEligibleSelectedProjectIds(getEligibleSelectedProjectIds(newTotalSelectedProjectIds));
    } else {
      wizardContext.setTotalSelectedProjectIds(selectedKeys);
      wizardContext.setEligibleSelectedProjectIds(getEligibleSelectedProjectIds(selectedKeys));
    }
  };

  useEffect(() => {
    if (wizardContext.bulkProviderTerminationMethod === BulkProviderTerminationMethod.Ghost) {
      props.formIsValid(wizardContext.eligibleSelectedProjectIds.length > 0 && !!wizardContext.selectedLineOfBusiness);
    } else {
      props.formIsValid(wizardContext.eligibleSelectedProjectIds.length > 0)
    }
  }, [wizardContext.eligibleSelectedProjectIds, wizardContext.selectedLineOfBusiness]);

  const updateSearchString = (newString: string) => {
    setSearchString(newString);
    setFilteredView(newString ? view : null);
  }

  return (
    <PageSection>
      <div className="container mt-3">
        <p className="copy-text-default">Select the projects you would like to terminate from.</p>
        <QESAlert type="info" classNames="mb-2" persist={true}>
          Any project that has current terminations will be overwritten if selected. Read only projects can not be selected.
        </QESAlert>


        {wizardContext.bulkProviderTerminationMethod === BulkProviderTerminationMethod.Ghost ?
          <fieldset>
            <div className="row">
              <div className="col">
                <p>Define what you wish to terminate on:</p>
                <div className="row">
                  <div className="col-4 pt-2">
                    <QESRadioGroup
                      renderInline={true}
                    >
                      <QESRadioButton
                        label="Ghost by LOB"
                        value="ghost"
                        name="termination-type"
                        checked={true}
                        testId="select_projects__termination-type--ghost"
                      />
                    </QESRadioGroup>
                  </div>
                  <div className="col-8 pe-5">
                    <QESSelect
                      className="bg-white"
                      containerClassName="mb-3"
                      label={null}
                      value={wizardContext.selectedLineOfBusiness}
                      isValid={!!wizardContext.selectedLineOfBusiness}
                      onChange={(e) => wizardContext.setSelectedLineOfBusiness(e.target.value)}

                      testId="ghost_termination__line_of_business">
                      {Object.values(ClaimsActivityLineOfBusinessTypeEnum)
                        .map((lineOfBusinessType, index) => (
                          <option
                            key={index}
                            value={lineOfBusinessType}
                          >
                            {lineOfBusinessType}
                          </option>
                        ))}
                    </QESSelect>
                  </div>
                </div>

              </div>
              <div className="col">
                <p>Additional Termination Refinement Options:</p>
                <div className="mt-n2">
                  <QESRadioGroup
                    renderInline={true}
                  >
                    <QESCheckbox
                      containerClassName="me-5"
                      label="All Locations"
                      checked={true}
                      className="mt-0"
                      testId="select_projects__termination-refinement-type--all-locations"
                      disabled={true}
                    />
                    <QESCheckbox
                      label="All Specialties"
                      checked={true}
                      className="mt-0"
                      testId="select_projects__termination-refinement-type--all-specialties"
                      disabled={true}
                    />
                  </QESRadioGroup>
                </div>
              </div>
            </div>
            <ProjectOrTagSearchBox
              filteredView={filteredView}
              inputRef={searchRef}
              searchString={searchString}
              setView={setView}
              updateSearchString={updateSearchString}
              view={view}
            />
          </fieldset>
          :
          <ProjectOrTagSearchBox
            filteredView={filteredView}
            inputRef={searchRef}
            searchString={searchString}
            setView={setView}
            updateSearchString={updateSearchString}
            view={view}
          />
        }
      </div>
      <div className="container">
        <div className="row">
          <div className="col-12">
            <p>{wizardContext.eligibleSelectedProjectIds.length} eligible {wizardContext.eligibleSelectedProjectIds.length === 1 ? "project" : "projects"} selected</p>
            {view === "projects" &&
              <Waffles<BulkProviderChangeProjectTableData>
                columns={projectsTableColumnConfig}
                data={renderedProjects}
                fullWidth={true}
                loading={wizardContext.projects.length === 0}
                handleCheckboxChange={handleProjectCheckboxChange}
                selectedKeys={wizardContext.eligibleSelectedProjectIds}
                showCheckboxes={true}
                testId="import-terminations-projects-table"
                totalNumberOfRecords={wizardContext.projects.length}
              />
            }

            {view === "tags" &&
              <Waffles<TagGroupData>
                columns={projectsTableColumnConfig}
                data={renderedTags}
                dataExpansions={renderedExpansionData}
                fullWidth={true}
                loading={!isTagApiCallResolved}
                handleCheckboxChange={handleTagCheckboxChange}
                selectedKeys={selectedTags}
                showCheckboxes={true}
                testId="select-projects__tags-table"
                totalNumberOfRecords={tagGroupData.length}
                showExpansionCheckboxesOnly={true}
                dataExpansionsHaveIndividualCheckboxes={true}
                indeterminateKeys={indeterminateTags}
              />
            }
          </div>
        </div>
      </div>
    </PageSection>
  );
};

export default TerminationsWizardSelectProjects;
